import React, { useCallback } from 'react';
import { useFormikContext } from 'formik';

import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { ContextSelector } from '../ContextSelector/context-selector.component';
import { buildContextSummaryParts } from '../ContextSelector/context-selector.helpers';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { SportTerminology } from '../../../lib/types';
import { IComposeContextSelection } from '../../../lib/types/communication.types';
import { IComposePanelFields } from '../ComposePanel/compose-panel.types';
import { buildAudienceFromSelections } from './audience-selector.helpers';
import { messages } from './audience-selector.messages';
import { IAudienceSelectorProps } from './audience-selector.types';
import { SelectorSurface, SummaryLine } from './audience-selector.styles';

export const AudienceSelector: React.FC<IAudienceSelectorProps> = ({
  assessmentId,
  assessmentName,
  skipOptionQueries,
  initialContexts,
  recipientCount,
  recipientCountLoading
}) => {
  const termLookup = useCompanySportTerminology();
  const { values, setValues } = useFormikContext<IComposePanelFields>();

  // SINGLE writer of both audience-shaped form fields: the chips and the
  // derived criteria update in one atomic setValues so they never disagree.
  const handleContextsChange = useCallback((contexts: IComposeContextSelection[]) => {
    setValues((prev) => ({
      ...prev,
      contexts,
      audience: buildAudienceFromSelections(contexts)
    }));
  }, [setValues]);

  // Not memoized: a short map/filter over a handful of chip rows, rendered
  // straight to JSX and not fed to any hook dep array.
  const summaryParts = buildContextSummaryParts(values.contexts, termLookup);

  // Pluralize against the plural forms while the debounced count is stale.
  const wordCount = recipientCountLoading ? 2 : recipientCount;
  const recipientSummary = (
    <IntlMsg
      {...messages.summaryRecipients}
      values={{
        countDisplay: recipientCountLoading ? '…' : recipientCount,
        athleteWord: termLookup[SportTerminology.ATHLETE].toLowerCase(),
        contactWord: pluralize(wordCount, 'contact'),
        notificationWord: pluralize(wordCount, 'a notification', 'notifications')
      }}
    />
  );

  return (
    <SelectorSurface>
      <ContextSelector
        contexts={values.contexts}
        initialContexts={initialContexts}
        onChange={handleContextsChange}
        assessmentId={assessmentId}
        assessmentName={assessmentName}
        skipOptionQueries={skipOptionQueries}
      />
      <SummaryLine>
        {summaryParts.length
          ? (
            <>
              <IntlMsg {...messages.summarySelection} values={{ parts: summaryParts.join(' · ') }} />
              {' — '}
              {recipientSummary}
            </>
          )
          : (
            <>
              <IntlMsg {...messages.summaryEveryone} />
              {' '}
              {recipientSummary}
            </>
          )}
      </SummaryLine>
    </SelectorSurface>
  );
};
